import { useIncidentAudio } from '../../hooks/useIncidentAudio'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiSpeakerWave } from 'react-icons/hi2'

import { Alert, Box, Button } from '@hivewatch/dark'

import { SettingsGroup, SettingsPolicyNote } from '@/features/settings'
import { useAppSelector } from '@/redux/hooks'
import { selectAreNotificationsMuted } from '@/redux/notifications/notificationsSlice'
import { trackEvent } from '@/utils/analytics'

import { AUDIO_SETTINGS_ANALYTICS } from '../../constants/constants'
import { SettingsToggleRow } from './SettingsToggleRow'

export const AudioSection = () => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const {
    toggleAudioEnabled,
    playAlertSound,
    isEnabled,
    isUpdatingAudioSetting,
    isCriticalSoundPolicyActive,
    isCriticalSoundPolicyLoaded,
  } = useIncidentAudio()
  // Every role that sees these audio controls is told which way the
  // organization's Critical-sound policy (ENG-35923) is set, so an operator
  // knows whether turning audio alerts off will also silence Critical
  // incidents. Held back until the policy is known: before that the value is a
  // `false` default, and the note would wrongly say the operator is in control.
  const shouldShowPolicyNote = isCriticalSoundPolicyLoaded
  const areNotificationsMuted = useAppSelector(selectAreNotificationsMuted)

  const testSound = async () => {
    trackEvent(AUDIO_SETTINGS_ANALYTICS.testAlert)
    await playAlertSound()
  }

  return (
    <SettingsGroup
      colorScheme={colorScheme}
      title={t('settings:rebrand_account_audio_group_title')}
    >
      <SettingsToggleRow
        colorScheme={colorScheme}
        dataTestId='audio-alerts'
        disabled={isUpdatingAudioSetting}
        isOn={isEnabled}
        label={t('settings:rebrand_account_audio_label')}
        onChange={() => void toggleAudioEnabled()}
      />

      {isEnabled && (
        <Alert
          colorScheme={colorScheme}
          description={t(
            isCriticalSoundPolicyActive
              ? 'settings:rebrand_account_audio_mute_override_policy_on_description'
              : 'settings:rebrand_account_audio_mute_override_description'
          )}
          title={t('settings:rebrand_account_audio_mute_override_title')}
          variant='info'
        />
      )}

      {shouldShowPolicyNote && (
        <SettingsPolicyNote
          colorScheme={colorScheme}
          description={t(
            isCriticalSoundPolicyActive
              ? 'settings:rebrand_account_audio_critical_policy_on_description'
              : 'settings:rebrand_account_audio_critical_policy_off_description'
          )}
          title={t(
            isCriticalSoundPolicyActive
              ? 'settings:rebrand_account_audio_critical_policy_on_title'
              : 'settings:rebrand_account_audio_critical_policy_off_title'
          )}
        />
      )}

      {isEnabled && areNotificationsMuted && (
        <Alert
          colorScheme={colorScheme}
          description={t(
            isCriticalSoundPolicyActive
              ? 'settings:rebrand_account_audio_override_enabled_policy_on_description'
              : 'settings:rebrand_account_audio_override_enabled_description'
          )}
          title={t('settings:rebrand_account_audio_override_enabled_title')}
          variant='warning'
        />
      )}

      {isEnabled && (
        <Box>
          <Button
            colorScheme={colorScheme}
            leftIcon={<HiSpeakerWave />}
            onClick={testSound}
            variant='secondary'
          >
            {t('settings:rebrand_account_audio_test_button')}
          </Button>
        </Box>
      )}
    </SettingsGroup>
  )
}
